// ============================================================
// CHARACTERISTIC TESTS - Pure d100 test engine for character
// characteristic and skill tests plus free-form dice rolls,
// with structured-narrative builders. Zero React dependency.
// ============================================================
import { rollD100, calcDoS, calcDoF } from './dice';
import { applyUnnaturalDos, DEFAULT_UNNATURAL_DOS_MODE } from './unnaturalDos';
import type { UnnaturalDosMode } from './unnaturalDos';
import { formatDiceResult } from './structuredNarrative';
import type { StructuredNarrativeEntry, ModifierDetail } from './structuredNarrative';
import type { Character, CharacteristicId, SkillId } from './character/types';
import { getEffectiveUnnaturalMultipliers } from './character/combatStatsResolver';
import { getSkillRating } from './character/normalize';
import { SKILL_CATALOG } from './character/skillCatalog';
import { collectSkillBonuses } from './character/effectiveTalents';
import type { DiceResult, Target } from '../types';

// ---- Difficulty ladder (Deathwatch RAW) ----

export interface DifficultyStep {
  id: string;
  label: string;
  mod: number;
}

export const DIFFICULTY_LADDER: DifficultyStep[] = [
  { id: 'trivial', label: 'Trivial', mod: 60 },
  { id: 'elementary', label: 'Elementary', mod: 50 },
  { id: 'simple', label: 'Simple', mod: 40 },
  { id: 'easy', label: 'Easy', mod: 30 },
  { id: 'routine', label: 'Routine', mod: 20 },
  { id: 'ordinary', label: 'Ordinary', mod: 10 },
  { id: 'challenging', label: 'Challenging', mod: 0 },
  { id: 'difficult', label: 'Difficult', mod: -10 },
  { id: 'hard', label: 'Hard', mod: -20 },
  { id: 'veryHard', label: 'Very Hard', mod: -30 },
  { id: 'arduous', label: 'Arduous', mod: -40 },
  { id: 'punishing', label: 'Punishing', mod: -50 },
  { id: 'hellish', label: 'Hellish', mod: -60 },
];

export const DEFAULT_DIFFICULTY_ID = 'challenging';

export function getDifficulty(id: string): DifficultyStep | undefined {
  return DIFFICULTY_LADDER.find((s) => s.id === id);
}

// ---- Test targets and rolls ----

export const CHARACTERISTIC_TEST_LABELS: Record<CharacteristicId, string> = {
  ws: 'WS',
  bs: 'BS',
  str: 'Str',
  tgh: 'Tgh',
  agi: 'Agi',
  int: 'Int',
  per: 'Per',
  wp: 'WP',
  fel: 'Fel',
};

export interface TestResult {
  label: string;
  roll: number;
  target: number;
  success: boolean;
  dos: number;
  dof: number;
  baseValue: number;
  difficultyMod: number;
  customMod: number;
  untrained?: boolean;
  /** Governing characteristic label - set for skill tests (untrained note). */
  governingLabel?: string;
  /** Labeled situational modifier folded into the target (e.g. parry weapon: Balanced +10). */
  extraMod?: { label: string; value: number };
  /** DoS added by the Unnatural house rule (0 when off/×1/failure). */
  unnaturalBonus?: number;
  /** Effective ×N used for the roll (override ?? stored). */
  unnaturalMultiplier?: number;
  /** House-rule note, e.g. "Unnatural ×2: +4 DoS (bonus rule)". */
  unnaturalNote?: string | null;
}

/** Options threading the Unnatural DoS house rule into a test roll. */
export interface UnnaturalTestOpts {
  /** Active house-rule mode; defaults to 'off'. */
  mode?: UnnaturalDosMode;
  /** Transient ×N override for this roll (sheet badge edits); wins over the stored multiplier. */
  multiplierOverride?: number;
  /**
   * Labeled situational modifier added to the test target and surfaced in
   * the narrative breakdown (v6.32.0 - the parry weapon selector).
   */
  extraMod?: { label: string; value: number };
}

/** Clamped 1..100, same bounds as calcModifiedSkill. */
export function calcTestTarget(baseValue: number, difficultyMod: number, customMod: number): number {
  return Math.min(Math.max(baseValue + difficultyMod + customMod, 1), 100);
}

/** Everything one d100 test needs besides the die. */
interface RollInputs {
  label: string;
  /** The base the target is built from, any extra modifier already folded in. */
  baseValue: number;
  difficultyMod: number;
  customMod: number;
  /** The characteristic value the Unnatural house rule denominates on. */
  charValue: number;
  multiplier: number;
  mode?: UnnaturalDosMode;
}

/** The Unnatural house rule applies on SUCCESS only; pass/fail and DoF stay native. */
function unnaturalOnSuccess(success: boolean, roll: number, target: number, inputs: RollInputs) {
  if (!success) return { dos: 0, bonus: 0, note: null };
  const mode = inputs.mode ?? DEFAULT_UNNATURAL_DOS_MODE;
  return applyUnnaturalDos(calcDoS(target, roll), roll, target, inputs.charValue, inputs.multiplier, mode);
}

/** Roll one d100 against the clamped target, with the Unnatural house rule on a success. */
function resolveRoll(inputs: RollInputs): Omit<TestResult, 'untrained' | 'governingLabel'> {
  const { label, baseValue, difficultyMod, customMod, multiplier } = inputs;
  const target = calcTestTarget(baseValue, difficultyMod, customMod);
  const roll = rollD100();
  const success = roll <= target;
  const unnatural = unnaturalOnSuccess(success, roll, target, inputs);
  return {
    label,
    roll,
    target,
    success,
    dos: unnatural.dos,
    dof: success ? 0 : calcDoF(roll, target),
    baseValue,
    difficultyMod,
    customMod,
    unnaturalBonus: unnatural.bonus,
    unnaturalMultiplier: multiplier,
    unnaturalNote: unnatural.note,
  };
}

/** What a test is rolled on: its label, pure base, rule value and multiplier, and any fields it adds. */
interface TestSpec {
  label: string;
  base: number;
  charValue: number;
  multiplier: number;
  /** Extra result fields, placed after `baseValue` (the skill's untrained flag and governing label). */
  fields?: Partial<TestResult>;
}

/**
 * Roll a test. The extra modifier is folded into the target but the
 * reported base stays pure - extraMod is reported separately, and only
 * when it is not zero.
 */
function rollTest(spec: TestSpec, difficultyMod: number, customMod: number, unnatural: UnnaturalTestOpts): TestResult {
  const extra = unnatural.extraMod?.value ?? 0;
  const rolled = resolveRoll({
    label: spec.label,
    baseValue: spec.base + extra,
    difficultyMod,
    customMod,
    charValue: spec.charValue,
    multiplier: spec.multiplier,
    mode: unnatural.mode,
  });
  const extraMod = unnatural.extraMod && unnatural.extraMod.value !== 0 ? { extraMod: unnatural.extraMod } : {};
  return { ...rolled, baseValue: spec.base, ...spec.fields, ...extraMod } as TestResult;
}

export function rollCharacteristicTest(
  character: Character,
  charId: CharacteristicId,
  difficultyMod: number,
  customMod: number,
  unnatural: UnnaturalTestOpts = {},
): TestResult {
  const c = character.characteristics[charId];
  // Fallback = effective multiplier (stored, floored by trait effects - v6.27.0)
  const multiplier = unnatural.multiplierOverride ?? getEffectiveUnnaturalMultipliers(character)[charId] ?? 1;
  const label = CHARACTERISTIC_TEST_LABELS[charId] + ' Test';
  return rollTest({ label, base: c.value, charValue: c.value, multiplier }, difficultyMod, customMod, unnatural);
}

// ---- Skill tests ----

export interface SkillTestDef {
  id: SkillId;
  label: string;
  governing: CharacteristicId;
  /** Advanced skills cannot be tested untrained. */
  advanced: boolean;
}

/** Full catalog (34 skills incl. dodge/parry), derived from skillCatalog (v6.30.0). */
export const SKILL_TEST_DEFS: SkillTestDef[] = SKILL_CATALOG.map((s) => ({
  id: s.id,
  label: s.name,
  governing: s.governing,
  advanced: s.advanced,
}));

export interface SkillTargetResult {
  base: number;
  untrained: boolean;
  /** False only for untrained ADVANCED skills - no deliberate test allowed. */
  rollable: boolean;
  /** Talent-column total folded into `base` when trained (0 untrained). */
  talentBonus: number;
}

/**
 * Trained: governing characteristic + tier bonuses (+10/+20) + talent + mod.
 * Untrained basic: half the governing characteristic - tier/talent/mod all
 * ignored (RAW basic-skill rule for deliberate tests, ADR 2026-07-19; the
 * defence chain's "cannot react untrained" convention governs reactions only).
 * Untrained advanced: not rollable at all (base 0).
 */
export function calcSkillTarget(character: Character, skillId: SkillId): SkillTargetResult {
  const def = SKILL_TEST_DEFS.find((d) => d.id === skillId)!;
  const charValue = character.characteristics[def.governing].value;
  const rating = getSkillRating(character, skillId);
  if (rating.trained) {
    const talentBonus = collectSkillBonuses(character, skillId).reduce((sum, b) => sum + b.value, 0);
    const tierBonus = (rating.plus10 ? 10 : 0) + (rating.plus20 ? 10 : 0);
    return {
      base: charValue + tierBonus + talentBonus + (rating.mod || 0),
      untrained: false,
      rollable: true,
      talentBonus,
    };
  }
  if (def.advanced) return { base: 0, untrained: true, rollable: false, talentBonus: 0 };
  return { base: Math.floor(charValue / 2), untrained: true, rollable: true, talentBonus: 0 };
}

export function rollSkillTest(
  character: Character,
  skillId: SkillId,
  difficultyMod: number,
  customMod: number,
  unnatural: UnnaturalTestOpts = {},
): TestResult {
  const def = SKILL_TEST_DEFS.find((d) => d.id === skillId)!;
  const { base, untrained, rollable } = calcSkillTarget(character, skillId);
  if (!rollable) {
    throw new Error(def.label + ' cannot be tested untrained (advanced skill).');
  }
  const governing = character.characteristics[def.governing];
  const multiplier = unnatural.multiplierOverride ?? getEffectiveUnnaturalMultipliers(character)[def.governing] ?? 1;
  const fields = { untrained, governingLabel: CHARACTERISTIC_TEST_LABELS[def.governing] };
  const spec = { label: def.label + ' Test', base, charValue: governing.value, multiplier, fields };
  return rollTest(spec, difficultyMod, customMod, unnatural);
}

// ---- Plain-target derived tests ----
// Targets not spawned from a Character have no characteristics map -
// only defence values and a toughness bonus. These deliberate tests
// derive d100 targets from those fields. Deliberately rollable even
// when canDodge/canParry are false: those flags govern REACTIONS in
// the defence chain, not deliberate tests (same family as the
// untrained-skill rule). Toughness base = TB × 10 without the
// unnatural multiplier - the established Toxic-effect convention.

export type PlainTargetTestId = 'dodge' | 'parry' | 'toughness' | 'strength' | 'willpower';

export interface PlainTargetTestDef {
  id: PlainTargetTestId;
  label: string;
}

export const PLAIN_TARGET_TEST_DEFS: PlainTargetTestDef[] = [
  { id: 'dodge', label: 'Dodge' },
  { id: 'parry', label: 'Parry' },
  { id: 'toughness', label: 'Tgh' },
  // v8.198.0: Strength and Willpower resists (Snare escapes, Pinning,
  // afflictions). Same bonus×10 convention as Toughness; the fields were
  // added to Target in v8.197.3 with a default of 3, hence the ?? guards.
  { id: 'strength', label: 'Str' },
  { id: 'willpower', label: 'WP' },
];

export function calcPlainTargetTestBase(target: Target, testId: PlainTargetTestId): number {
  if (testId === 'dodge') return target.dodge.agility;
  if (testId === 'parry') return target.parry.weaponSkill;
  if (testId === 'strength') return (target.strengthBonus ?? 3) * 10;
  if (testId === 'willpower') return (target.willpowerBonus ?? 3) * 10;
  return target.toughnessBonus * 10;
}

/**
 * The stored ×N governing a plain-target derived test. Legacy targets
 * predate the unnatural fields - hence the || 1 guards. Strength and
 * Willpower answer their own multipliers since v8.310.11; before that
 * they answered ×1 because their bonuses already carry the multiplier,
 * which left a statblock's Unnatural Strength worth zero degrees in
 * every mode. The double count is avoided in `rollPlainTargetTest`
 * instead, by denominating on the BASE bonus.
 */
export function plainTargetTestMultiplier(target: Target, testId: PlainTargetTestId): number {
  if (testId === 'toughness') return target.unnaturalToughness || 1;
  if (testId === 'dodge') return target.unnaturalAgility || 1;
  if (testId === 'strength') return target.unnaturalStrength || 1;
  if (testId === 'willpower') return target.unnaturalWillpower || 1;
  return target.unnaturalWeaponSkill || 1;
}

export function rollPlainTargetTest(
  target: Target,
  testId: PlainTargetTestId,
  difficultyMod: number,
  customMod: number,
  unnatural: UnnaturalTestOpts = {},
): TestResult {
  const def = PLAIN_TARGET_TEST_DEFS.find((d) => d.id === testId)!;
  const base = calcPlainTargetTestBase(target, testId);
  const multiplier = unnatural.multiplierOverride ?? plainTargetTestMultiplier(target, testId);
  // The derived base doubles as the characteristic value: for Tgh (TB×10),
  // 'bonus' mode yields (N−1)×TB - exactly the bonus-denominated intent.
  // Strength and Willpower are stored EFFECTIVE (SB×N), so the value the
  // rule denominates on is the base bonus: SB 8 ×2 → 4 → +4 DoS, the same
  // answer a character with SB 4 ×2 gets (v8.310.11). The test target
  // itself stays the effective bonus × 10, as it has since v8.198.0.
  const stored = testId === 'strength' || testId === 'willpower' ? plainTargetTestMultiplier(target, testId) : 1;
  const charValue = stored > 1 ? base / stored : base;
  return rollTest({ label: def.label + ' Test', base, charValue, multiplier }, difficultyMod, customMod, unnatural);
}

// ---- Narrative builders ----

function fmtSigned(n: number): string {
  return n >= 0 ? '+' + n : String(n);
}

function difficultyText(result: TestResult): string {
  const step = DIFFICULTY_LADDER.find((s) => s.mod === result.difficultyMod);
  const diff = step ? step.label + ' ' + fmtSigned(step.mod) : fmtSigned(result.difficultyMod);
  return result.customMod !== 0 ? diff + ', ' + fmtSigned(result.customMod) : diff;
}

/** "Success (3 DoS, incl. Unnatural ×2 +2)" or "Failure (2 DoF)". */
function outcomeText(result: TestResult): string {
  if (!result.success) return `Failure (${result.dof} DoF)`;
  const unnatural =
    (result.unnaturalBonus || 0) > 0
      ? `, incl. Unnatural ×${result.unnaturalMultiplier} +${result.unnaturalBonus}`
      : '';
  return `Success (${result.dos} DoS${unnatural})`;
}

/** The modifier rows: base, difficulty, then custom and the extra modifier when present. */
function testModifiers(result: TestResult): ModifierDetail[] {
  const baseLabel = result.untrained
    ? `Base (untrained: half ${result.governingLabel})`
    : 'Base ' + result.label.replace(' Test', '');
  const step = DIFFICULTY_LADDER.find((s) => s.mod === result.difficultyMod);
  const modifiers: ModifierDetail[] = [
    { label: baseLabel, value: result.baseValue },
    { label: step ? `Difficulty (${step.label})` : 'Difficulty', value: result.difficultyMod },
  ];
  if (result.customMod !== 0) modifiers.push({ label: 'Custom', value: result.customMod });
  if (result.extraMod) modifiers.push({ label: result.extraMod.label, value: result.extraMod.value });
  return modifiers;
}

export function buildTestNarrative(result: TestResult, characterName: string): StructuredNarrativeEntry {
  const untrainedNote = result.untrained ? `Untrained: half ${result.governingLabel}` : null;
  const notes = [untrainedNote, result.unnaturalNote].filter((n): n is string => !!n);
  return {
    summary:
      `${characterName}: ${result.label} (${difficultyText(result)}) - ` +
      `rolled ${result.roll} vs ${result.target} - ${outcomeText(result)}`,
    detail: {
      rolls: [{ label: result.label, roll: result.roll, target: result.target, success: result.success }],
      modifiers: testModifiers(result),
      ...(notes.length ? { specialNotes: notes } : {}),
    },
    outcome: result.success ? 'hit' : 'miss',
  };
}

export function buildDiceRollNarrative(expr: string, result: DiceResult): StructuredNarrativeEntry {
  return {
    summary: `🎲 ${expr} = ${result.total}`,
    detail: { specialNotes: [formatDiceResult(result)] },
  };
}
